// @owners { team: patients-team }
import { ActionSheetContext, Button, Card, Column, ListDescription, ListItem } from '@alto/design-system';
import React, { useContext, useEffect } from 'react';
// eslint-disable-next-line import/no-deprecated
import { fetchUsers } from '~shared/actions/users';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import { PREFERRED_COMMUNICATION_OPTIONS, getSecondaryContactLabel } from '~shared/helpers/communicationPreferences';
import { formatPhone } from '~shared/helpers/helper';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type PreferredCommunicationMethod } from '~shared/types';
import { CommunicationPreferencesActionSheet } from './CommunicationPreferencesActionSheet';
import { ContactInfoWithAuth } from './ContactInfoWithAuth';
import { Breadcrumbs } from '~web/components/Breadcrumbs';
import { PageBackground } from '~web/components/Page';
import { PageHeader } from '~web/components/PageHeader';

const BREADCRUMBS = [
  {
    text: 'Account',
    to: '/me',
  },
  {
    text: 'Contact & Login Info',
  },
];

export const ProfileContactInfo = () => {
  const user = useSelectorShared(getCurrentUser);
  const dispatch = useDispatchShared();
  const { setActiveActionSheet } = useContext(ActionSheetContext);

  useEffect(() => {
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchUsers());
  }, [dispatch]);

  const preferredContact = PREFERRED_COMMUNICATION_OPTIONS.find(
    (option) => option.value === user?.preferred_communication,
  );
  const secondaryContactLabel = getSecondaryContactLabel(
    preferredContact?.value as PreferredCommunicationMethod | undefined,
    user?.phi_auth_info,
    user?.secondary_communication_preference,
  );

  return (
    <PageBackground>
      <Column gap="MD">
        <Breadcrumbs breadcrumbs={BREADCRUMBS} />
        <PageHeader>Contact & Login Info</PageHeader>
        <Card
          title="Phone Number and Login"
          RightContent={
            <Button
              small
              type="tertiary"
              label="EDIT"
              onPress={() => {
                setActiveActionSheet(<ContactInfoWithAuth />);
              }}
            />
          }
        >
          <ListItem
            title="Phone number"
            descriptions={
              <ListDescription>{formatPhone(user?.phone || '') || 'No phone number on file'}</ListDescription>
            }
          />
          <ListItem
            title="Alternate number"
            descriptions={
              <ListDescription>
                {formatPhone(user?.secondary_contact_phone || '') || 'No secondary phone on file'}
              </ListDescription>
            }
          />
          <ListItem
            title="Email & login"
            descriptions={<ListDescription>{user?.email || ''}</ListDescription>}
          />
          <ListItem
            title="Password"
            descriptions={<ListDescription>************</ListDescription>}
          />
        </Card>
        <Card
          title="Communication Preferences"
          RightContent={
            <Button
              width="inline"
              label="EDIT"
              type="tertiary"
              onPress={() => {
                setActiveActionSheet(<CommunicationPreferencesActionSheet />);
              }}
            />
          }
        >
          <ListItem
            title="Preferred contact method"
            descriptions={
              <ListDescription>
                {preferredContact?.label ? `${preferredContact.label} ${secondaryContactLabel}` : 'unspecified'}
              </ListDescription>
            }
          />
        </Card>
      </Column>
    </PageBackground>
  );
};
